import React, { useState } from 'react';
import { Minus, Plus, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { fmtUsd } from '../../lib/tax';

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const Counter = ({ value, max, onChange, testId }) => (
  <div className="flex items-center gap-2" data-testid={testId}>
    <span className="text-sm text-neutral-600">Sections:</span>
    <button type="button" onClick={() => onChange(Math.max(0, value - 1))} disabled={value === 0}
      className="w-10 h-10 sm:w-8 sm:h-8 rounded-sm border border-neutral-300 flex items-center justify-center disabled:opacity-40 hover:border-red-600"
      data-testid={`${testId}-minus`} aria-label="Fewer sections">
      <Minus className="w-4 h-4" />
    </button>
    <span className="w-6 text-center font-semibold tabular-nums" data-testid={`${testId}-value`}>{value}</span>
    <button type="button" onClick={() => onChange(Math.min(max, value + 1))} disabled={value === max}
      className="w-10 h-10 sm:w-8 sm:h-8 rounded-sm border border-neutral-300 flex items-center justify-center disabled:opacity-40 hover:border-red-600"
      data-testid={`${testId}-plus`} aria-label="More sections">
      <Plus className="w-4 h-4" />
    </button>
  </div>
);

const Toggle = ({ on, onChange, testId }) => (
  <button type="button" onClick={() => onChange(on ? 0 : 1)} data-testid={testId} aria-pressed={on}
    className={`w-9 h-9 sm:w-7 sm:h-7 shrink-0 rounded-sm border-2 flex items-center justify-center transition-colors ${on ? 'bg-red-600 border-red-600' : 'bg-white border-neutral-300 hover:border-red-600'}`}>
    {on && <Check className="w-4 h-4 text-white" />}
  </button>
);

// Optional Square add-ons. `initial` / onContinue value: { [modifier_id]: quantity }.
export const StepAddons = ({ service, initial, onContinue, onBack }) => {
  const [qty, setQty] = useState(initial || {});

  const setOption = (group, opt, value) => setQty((prev) => {
    const next = { ...prev };
    if (group.selection === 'SINGLE') group.options.forEach((o) => { delete next[o.modifier_id]; });
    if (value > 0) next[opt.modifier_id] = value; else delete next[opt.modifier_id];
    return next;
  });

  const addonsTotal = service.addons
    .flatMap((g) => g.options)
    .reduce((sum, o) => sum + o.price_cents * (qty[o.modifier_id] || 0), 0);

  return (
    <div className="space-y-4" data-testid="step-addons">
      <p className="text-neutral-600 text-sm">Optional upgrades. Skip if you don&apos;t need any.</p>
      {service.addons.map((group) => (
        <div key={group.list_id} className="border border-neutral-200 rounded-sm p-4" data-testid={`addon-group-${slug(group.name)}`}>
          <div className="font-display uppercase font-bold text-neutral-950 mb-2">{group.name}</div>
          {group.options.map((opt) => {
            const value = qty[opt.modifier_id] || 0;
            const testId = `addon-${slug(group.name)}`;
            return (
              <div key={opt.modifier_id} className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-neutral-800 text-sm">{opt.name}</div>
                  <div className="text-neutral-500 text-xs">
                    {fmtUsd(opt.price_cents)}{opt.max_qty > 1 ? ` each, up to ${opt.max_qty}` : ''}
                    {value > 0 && opt.max_qty > 1 && <span className="text-red-600 font-semibold"> = {fmtUsd(opt.price_cents * value)}</span>}
                  </div>
                </div>
                {opt.max_qty > 1
                  ? <Counter value={value} max={opt.max_qty} onChange={(v) => setOption(group, opt, v)} testId={testId} />
                  : <Toggle on={value > 0} onChange={(v) => setOption(group, opt, v)} testId={testId} />}
              </div>
            );
          })}
        </div>
      ))}
      <div className="flex items-center justify-between pt-2">
        <span className="text-sm text-neutral-600">Add-ons: <span className="font-semibold text-neutral-950" data-testid="addons-total">{fmtUsd(addonsTotal)}</span></span>
        <div className="flex gap-2">
          {onBack && <Button type="button" variant="outline" onClick={onBack} className="border-neutral-300 text-neutral-900 bg-white hover:bg-neutral-100" data-testid="step-addons-back">Back</Button>}
          <Button type="button" onClick={() => onContinue(qty)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="step-addons-continue">Continue</Button>
        </div>
      </div>
    </div>
  );
};
